import { useState } from "react";
import { AlertTriangle, Clock, Lock, Mail, X } from "lucide-react";
import Modal, { ModalSize } from "./modal/Modal";
import type { Trial } from "@/hooks/useMyTrial";

type BannerVariant =
  | "active"
  | "week-left"
  | "ending-soon"
  | "expired"
  | "revoked";

const ENDING_SOON_THRESHOLD_DAYS = 3;
const WEEK_LEFT_THRESHOLD_DAYS = 7;

function variantFor(trial: Trial): BannerVariant {
  if (trial.status === "revoked") return "revoked";
  if (trial.status === "expired" || trial.days_left <= 0) return "expired";
  if (trial.days_left <= ENDING_SOON_THRESHOLD_DAYS) return "ending-soon";
  if (trial.days_left <= WEEK_LEFT_THRESHOLD_DAYS) return "week-left";
  return "active";
}

export function isTrialLocked(trial: Trial): boolean {
  const v = variantFor(trial);
  return v === "expired" || v === "revoked";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function progressPercent(trial: Trial): number {
  const total = trial.length_days || 14;
  const elapsed = total - trial.days_left;
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

const VARIANT_STYLES: Record<
  BannerVariant,
  {
    wrapper: string;
    title: string;
    iconBg: string;
    progressFill: string;
    btnStyle: string;
    showButton: boolean;
    showProgress: boolean;
  }
> = {
  active: {
    wrapper:
      "bg-gradient-to-r from-[#F1ECDC] to-[#E8F3EC] border border-[#D7CEB1]",
    title: "text-[#1F6B3E] font-bold",
    iconBg: "bg-[#1F6B3E]",
    progressFill: "bg-[#1F6B3E]",
    btnStyle:
      "border border-[#1F6B3E] text-[#1F6B3E] bg-white hover:bg-[#E8F3EC]",
    showButton: true,
    showProgress: true,
  },
  "week-left": {
    wrapper:
      "bg-gradient-to-r from-[#FFFBEC] to-[#FFF1CF] border border-[#F5DA9A]",
    title: "text-[#8A5A12] font-bold",
    iconBg: "bg-[#C57E14]",
    progressFill: "bg-[#C57E14]",
    btnStyle:
      "border border-[#1F6B3E] text-[#1F6B3E] bg-white hover:bg-[#E8F3EC]",
    showButton: true,
    showProgress: true,
  },
  "ending-soon": {
    wrapper:
      "bg-gradient-to-r from-[#FFF4E0] to-[#FFE7C2] border border-[#F5C97A]",
    title: "text-[#B86E15] font-bold",
    iconBg: "bg-[#B86E15]",
    progressFill: "bg-[#E5A23A]",
    btnStyle: "bg-[#1F6B3E] text-white hover:bg-[#155028]",
    showButton: true,
    showProgress: true,
  },
  expired: {
    wrapper:
      "bg-gradient-to-r from-[#FAFAFA] to-[#F1F1F1] border border-[#DEDEDE]",
    title: "text-[#1F2937] font-bold",
    iconBg: "bg-[#4B5563]",
    progressFill: "bg-[#4B5563]",
    btnStyle: "bg-[#1F6B3E] text-white hover:bg-[#155028]",
    showButton: true,
    showProgress: false,
  },
  revoked: {
    wrapper:
      "bg-gradient-to-r from-[#FAFAFA] to-[#F1F1F1] border border-[#DEDEDE]",
    title: "text-[#1F2937] font-bold",
    iconBg: "bg-[#4B5563]",
    progressFill: "bg-[#4B5563]",
    btnStyle: "bg-[#1F6B3E] text-white hover:bg-[#155028]",
    showButton: true,
    showProgress: false,
  },
};

function titleFor(trial: Trial, variant: BannerVariant): string {
  switch (variant) {
    case "active":
      return `You're on a ${trial.length_days}-day trial`;
    case "week-left":
      return "One week left on your trial";
    case "ending-soon":
      return `Your trial ends in ${trial.days_left} day${trial.days_left === 1 ? "" : "s"}`;
    case "expired":
      return "Your trial has ended";
    case "revoked":
      return "Your trial has ended";
  }
}

function subtitleFor(
  trial: Trial,
  variant: BannerVariant,
  isStudent = false,
): string {
  switch (variant) {
    case "active":
      return `${trial.days_left} days left · trial ends ${formatDate(trial.trial_end)}`;
    case "week-left":
      return `${trial.days_left} days left · trial ends ${formatDate(trial.trial_end)}`;
    case "ending-soon":
      return isStudent
        ? `Trial ends ${formatDate(trial.trial_end)}.`
        : `Trial ends ${formatDate(trial.trial_end)}. Email the EverTutor team to extend.`;
    case "expired":
    case "revoked":
      return isStudent
        ? "Ask your teacher to bring it back."
        : "Email the EverTutor team to continue.";
  }
}

function progressLabelFor(trial: Trial, variant: BannerVariant): string {
  switch (variant) {
    case "active":
      return "Trial progress";
    case "week-left":
      return `${trial.days_left} of ${trial.length_days} days left`;
    case "ending-soon":
      return `${trial.days_left} of ${trial.length_days} days left`;
    default:
      return "";
  }
}

function IconFor({
  variant,
  className,
}: {
  variant: BannerVariant;
  className: string;
}) {
  switch (variant) {
    case "active":
    case "week-left":
      return <Clock className={className} />;
    case "ending-soon":
      return <AlertTriangle className={className} />;
    case "expired":
    case "revoked":
      return <Lock className={className} />;
  }
}

function buttonLabelFor(variant: BannerVariant): string {
  if (variant === "expired" || variant === "revoked") return "Contact us";
  return "Request extension";
}

interface TrialBannerProps {
  trial: Trial;
  showButton?: boolean;
  isStudent?: boolean;
}

export default function TrialBanner({
  trial,
  showButton = true,
  isStudent = false,
}: TrialBannerProps) {
  const [extensionOpen, setExtensionOpen] = useState(false);
  const variant = variantFor(trial);
  const styles = VARIANT_STYLES[variant];
  const pct = progressPercent(trial);

  return (
    <>
      <div
        className={`w-full rounded-[18px] px-4 py-3 mb-6 flex items-center gap-3 ${styles.wrapper}`}
        role={isTrialLocked(trial) ? "alert" : "status"}
      >
        {/* Icon */}
        <div
          className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white ${styles.iconBg}`}
        >
          <IconFor variant={variant} className="w-5 h-5" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${styles.title}`}>
            {titleFor(trial, variant)}
          </div>
          <div className="text-xs text-gray-600 mt-0.5">
            {subtitleFor(trial, variant, isStudent)}
          </div>
        </div>

        {/* Progress bar (active / week-left / ending-soon) */}
        {styles.showProgress && (
          <div className="flex-shrink-0 flex flex-col items-end gap-1 hidden sm:flex">
            <span className="text-xs text-gray-500">
              {progressLabelFor(trial, variant)}
            </span>
            <div className="w-44 h-1.5 bg-white/70 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${styles.progressFill}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* CTA button */}
        {styles.showButton && showButton && (
          <button
            onClick={() => setExtensionOpen(true)}
            className={`flex-shrink-0 rounded-[10px] px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${styles.btnStyle}`}
          >
            <Mail className="w-4 h-4" />
            {buttonLabelFor(variant)}
          </button>
        )}
      </div>

      <RequestExtensionModal
        isOpen={extensionOpen}
        onClose={() => setExtensionOpen(false)}
      />
    </>
  );
}

function RequestExtensionModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={ModalSize.sm}
      showCloseButton={false}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
        <h3 className="text-xl font-bold text-gray-900">Need more time?</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex items-center gap-5 px-6 py-6 border-b border-gray-100">
        <div className="w-16 h-16 rounded-2xl bg-[#E8F3EC] text-[#1F6B3E] flex items-center justify-center flex-shrink-0">
          <Mail className="w-7 h-7" />
        </div>
        <p className="text-base leading-relaxed text-gray-700">
          Reach out to{" "}
          <strong className="text-gray-900">kartik@evertutor.ai</strong> and we'll
          be happy to extend your trial.
        </p>
      </div>

      {/* Footer */}
      <div className="flex justify-end px-6 py-4 bg-[#FAFAF6]">
        <button
          onClick={onClose}
          className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
